/**
 * Pure health-band logic for the Unified forecast chart.
 *
 * A balance value is classified against an account's comfort/floor thresholds:
 *   comfort  — value ≥ comfort  (teal / `--chart-forecast`)
 *   caution  — floor ≤ value < comfort  (amber)
 *   danger   — value < floor  (red)
 *
 * Boundaries are inclusive at comfort and floor, matching the prototype's
 * `healthColor()` (ForecastChartProtoV2.svelte).
 *
 * **`null` means absent — not `≤ 0` (#1646).** The gate used to be `> 0`, which
 * treated "positive" as a proxy for "set". A credit account's floor IS its limit
 * and is stored NEGATIVE (§3.1/§3.5), so that proxy deleted the danger band for
 * every credit-bearing selection. `null` is already what the data model means by
 * unset (a blank field sends `null`), so the display layer now says the same
 * thing. A real `$0` floor is a live threshold, not an absent one.
 *
 * **Multi-account scope classifies per account, then takes the worst** —
 * `worstBand` / `bandsForPoints`. Thresholds are never summed: a floor is a
 * per-account promise, not an additive quantity, and a `+400` checking floor
 * summed with a `−1000` credit limit yields a number describing neither. This
 * mirrors `pickWorstFloorAnalysis` (`floor-analysis.ts`) and `pickSoonestRunway`
 * (`runway.ts`) — the shipped precedent is to pick the worst account, never to
 * blend them.
 *
 * **Classification reads each week's LOW POINT, not its close (#1757).** A week
 * that closes at $3,431 after bottoming at $194 on the Tuesday is a floor breach,
 * and banding it on the close painted it green. The GEOMETRY may still draw the
 * close for a multi-account scope — a sum of per-account lows landing on
 * different days is not a balance anyone ever held — but the BAND is always the
 * truth about the deepest point.
 *
 * The actual/projected/scenario merge lives here too, applied PER ACCOUNT
 * (`readingsFromPoints`). If the bands were derived from baseline balances while
 * the line drew a simulated trajectory, the colour and the geometry would
 * describe different series — the same incoherence #1634 removed.
 *
 * No canvas/DOM here — the CanvasGradient assembly lives in the builder.
 */

export type HealthBand = 'comfort' | 'caution' | 'danger';

export interface HealthThresholds {
	/** `null` ⇒ absent (no comfort band). A real `0` is a live threshold. */
	comfort: number | null;
	/** `null` ⇒ absent (no danger band). Negative is a credit limit (§3.1). */
	floor: number | null;
}

/** Nothing set — the pair a threshold-less scope is drawn against. */
export const NO_THRESHOLDS: HealthThresholds = { comfort: null, floor: null };

/**
 * One in-scope account's contribution to a week, as the aggregation emits it
 * (#1646). Carried ON the `ChartDataPoint` rather than beside it: #1634
 * established that nothing may rewrite the points array between the dashboard
 * page and either surface that reads it, and anything travelling alongside can
 * desynchronise from it.
 */
export interface AccountPoint extends HealthThresholds {
	accountId: string;
	accountName: string;
	/** This account's OWN projected balance for the week — never the sum. */
	projected: number;
	/** This account's reconciled balance, when the week has one. */
	actual?: number;
	/**
	 * This account's LOW POINT for the week — its minimum end-of-day balance,
	 * never its close (#1757). **This is the classification input**: a floor is
	 * crossed by the deepest point of a week, not by where it happens to land on
	 * the Saturday. Absent ⇒ classification falls back to `projected`, which is
	 * the pre-#1757 behaviour.
	 */
	low?: number;
	/** `low`'s sibling on the cleared line, present iff `actual` is. */
	lowActual?: number;
	/** ISO date of `low` — the DAY the week bottoms out. */
	lowDate?: string;
}

/** An account's resolved balance for one week, ready to classify. */
export interface AccountReading extends HealthThresholds {
	accountId: string;
	accountName: string;
	/** The value classified — the week's low where the engine supplied one (#1757). */
	balance: number;
	/** ISO date `balance` lands on, when it is a low. Lets a surface name the DAY. */
	lowDate?: string;
}

/** A week's band plus the account that produced it (`null` when nobody voted). */
export interface GovernedBand {
	band: HealthBand | null;
	governedBy: AccountReading | null;
}

const NO_BAND: GovernedBand = { band: null, governedBy: null };

/** Severity order — the worst band across accounts wins the week. */
const BAND_RANK: Record<HealthBand, number> = { comfort: 1, caution: 2, danger: 3 };

export interface HealthColors {
	/** comfort band — typically `ChartColors.forecast` */
	forecast: string;
	/** caution band — `ChartColors.chartCaution` */
	caution: string;
	/** danger band — `ChartColors.chartDanger` */
	danger: string;
	/**
	 * The "cannot classify" tone (#1646) — a neutral, NOT `forecast`. Optional so
	 * existing callers keep compiling; when omitted an unbandable week falls back
	 * to `forecast`, which is the pre-#1646 behaviour and reads as health the app
	 * has not earned. Pass it.
	 */
	neutral?: string;
}

/**
 * Classify a value into a health band, or `null` when both thresholds are
 * absent (caller must render that as "cannot classify", never as comfort).
 *
 * Single-threshold behaviour:
 *  - only floor set   → ≥ floor ⇒ comfort, else danger (no caution band)
 *  - only comfort set → ≥ comfort ⇒ comfort, else caution (no danger band)
 *
 * `danger` is tested before `comfort` so an inverted pair (`floor > comfort`)
 * reports the breach rather than silently reporting health. The backend enforces
 * `comfort ≥ floor` on both create and update, so this is a guard on malformed
 * data, not a path real rows take.
 */
export function healthBand(v: number, t: HealthThresholds): HealthBand | null {
	const { comfort, floor } = t;
	if (comfort == null && floor == null) return null;
	if (floor != null && v < floor) return 'danger';
	if (comfort != null && v >= comfort) return 'comfort';
	if (comfort == null) return 'comfort'; // only floor set, value ≥ floor ⇒ healthy
	return 'caution';
}

/**
 * The worst band across a week's accounts, and which account produced it
 * (#1646).
 *
 * An account with neither threshold set casts no vote — it can no more make the
 * week look healthy than it can make it look risky. When every account abstains
 * the result is `{ band: null }`.
 *
 * Ties go to the first account in the array. That only decides which NAME is
 * shown for an identical band, never the band itself.
 */
export function worstBand(readings: ReadonlyArray<AccountReading>): GovernedBand {
	let worst: GovernedBand = NO_BAND;
	for (const reading of readings) {
		const band = healthBand(reading.balance, reading);
		if (band == null) continue; // no threshold ⇒ no vote
		if (worst.band == null || BAND_RANK[band] > BAND_RANK[worst.band]) {
			worst = { band, governedBy: reading };
		}
	}
	return worst;
}

/**
 * The worst band across an already-classified run of weeks (#1622).
 *
 * Reuses the same `BAND_RANK` `worstBand` votes with, so the codebase carries
 * ONE severity ordering. A horizon-aware render mode that groups weeks into bins
 * must colour each bin with this — any other reducer (mean, first, endpoint) can
 * only ever SOFTEN a bin containing a bad week, which is exactly what the #1634
 * strip↔chart agreement invariant forbids.
 *
 * `null` entries abstain, matching `worstBand`: a week nobody could classify
 * neither helps nor hurts the bin. All-null ⇒ `null`.
 */
export function worstOfBands(bands: ReadonlyArray<HealthBand | null>): HealthBand | null {
	let worst: HealthBand | null = null;
	for (const band of bands) {
		if (band == null) continue;
		if (worst == null || BAND_RANK[band] > BAND_RANK[worst]) worst = band;
	}
	return worst;
}

/** What this module can classify. `ChartDataPoint` structurally satisfies it. */
export interface BandablePoint {
	projected: number;
	actual?: number;
	/** Portfolio low — set only for a single-account scope (see `buildAggregatedChartData`). */
	low?: number;
	lowActual?: number;
	lowDate?: string;
	comfort?: number | null;
	floor?: number | null;
	accounts?: ReadonlyArray<AccountPoint>;
}

/** How a series resolves each account's balance — the same merge the line draws. */
export interface ReadingOpts {
	todayIndex?: number;
	showActual?: boolean;
	/** Per-week, per-account simulated balances. Present ⇒ the sim governs. */
	scenarioAccountsByWeek?: ReadonlyArray<ReadonlyArray<{ accountId: string; balance: number }>>;
	/** Portfolio-level simulated values — only used on the no-`accounts` path. */
	scenarioByWeek?: ReadonlyArray<number | null>;
}

/** actual only in the past when present + enabled, else projected. */
function merged(projected: number, actual: number | undefined, past: boolean, showActual: boolean) {
	return showActual && past && actual != null ? actual : projected;
}

/**
 * Resolve every week's per-account readings (#1646) — the single place the
 * actual/projected/scenario merge is applied for classification.
 *
 * Points without `accounts` fall back to one synthetic reading over the
 * portfolio total and the point's own scalar pair. That path covers hand-built
 * fixtures and any caller predating #1646, and it degrades to exactly the
 * single-account behaviour rather than to "healthy".
 */
export function readingsFromPoints(
	points: ReadonlyArray<BandablePoint>,
	opts: ReadingOpts = {}
): AccountReading[][] {
	const todayIndex = opts.todayIndex ?? 0;
	const showActual = opts.showActual ?? true;
	const scenarioAccounts = opts.scenarioAccountsByWeek;
	const scenarioTotals = opts.scenarioByWeek;

	// THRESHOLDS COME FROM `points[0]`, balances from each week (#1634 preserved
	// through #1646). comfort/floor are account properties, not per-week ones, so
	// every point carries the same pair and the first is the authority. Reading
	// them per-point would let anything that re-stamped one week silently move
	// that week's band — the exact class of bug #1634 closed by making both
	// surfaces read one place. Only the BALANCE varies week to week.
	const first = points[0];
	const thresholdsById = new Map<string, HealthThresholds>(
		(first?.accounts ?? []).map((a) => [a.accountId, { comfort: a.comfort, floor: a.floor }])
	);
	const firstScalar: HealthThresholds = {
		comfort: first?.comfort ?? null,
		floor: first?.floor ?? null
	};

	return points.map((p, i) => {
		const past = todayIndex >= 0 && i < todayIndex;
		if (p.accounts?.length) {
			const sim = scenarioAccounts?.[i];
			return p.accounts.map((a) => {
				const t = thresholdsById.get(a.accountId) ?? { comfort: a.comfort, floor: a.floor };
				return {
					accountId: a.accountId,
					accountName: a.accountName,
					comfort: t.comfort,
					floor: t.floor,
					// #1757: classify on the week's LOW, falling back to its close. Both
					// lenses fall back together — banding a future week on its low while
					// banding a past week on its close would put a step in the strip at
					// today that no balance ever took.
					balance:
						sim?.find((s) => s.accountId === a.accountId)?.balance ??
						merged(a.low ?? a.projected, a.lowActual ?? a.actual, past, showActual),
					lowDate: a.lowDate
				};
			});
		}
		return [
			{
				accountId: '',
				accountName: '',
				comfort: firstScalar.comfort,
				floor: firstScalar.floor,
				balance:
					scenarioTotals?.[i] ??
					merged(p.low ?? p.projected, p.lowActual ?? p.actual, past, showActual),
				lowDate: p.lowDate
			}
		];
	});
}

/**
 * **The classification seam (#1634, #1646).** The forecast chart and the
 * dashboard's runway strip both call this over the same array, so agreement
 * between them is a property of the code rather than a coincidence between two
 * derivations.
 */
export function bandsForPoints(
	points: ReadonlyArray<BandablePoint>,
	opts: ReadingOpts = {}
): GovernedBand[] {
	return readingsFromPoints(points, opts).map(worstBand);
}

/**
 * **Draw-only (#1646).** The one comfort/floor pair worth drawing a hairline at:
 * defined per field iff EVERY in-scope account sets it and they all agree on
 * the value — an account that sets none is not abstaining, it is a part of the
 * scope the value does not describe. Never the classification input — a per-account promise has no position
 * on a summed y-axis, which is exactly what the old summed threshold pretended.
 *
 * Read from the FIRST point only; comfort/floor are account properties, not
 * per-week ones, so every point carries the same set.
 *
 * When this comes back `null` but a band still exists, the draw layer falls back
 * to the GOVERNING account's own threshold, labelled with that account's name —
 * so the hairline can never be misread as a portfolio-level level.
 */
export function drawableThresholds(points: ReadonlyArray<BandablePoint>): HealthThresholds {
	const first = points[0];
	if (!first) return NO_THRESHOLDS;
	if (!first.accounts?.length) {
		return { comfort: first.comfort ?? null, floor: first.floor ?? null };
	}
	return drawableFromAccounts(first.accounts);
}

/**
 * The drawable pair for one week's accounts — a field is drawable iff **every**
 * in-scope account sets it AND they all agree on the value. Exported so the
 * aggregation stamps the same rule it will later be read back through, rather
 * than a lookalike.
 *
 * "Every", not "every account that sets it": an account with no floor is not
 * abstaining from a shared value, it is a part of the scope that the value does
 * not describe. Chequing (floor $400) beside Savings (no floor) has no
 * portfolio-level floor — drawing `$400` under their $25,000 combined balance,
 * or printing it beside that balance in the hero, is the same category error as
 * the summed threshold this issue removed, just arrived at differently.
 *
 * A single-account scope trivially satisfies this, so the common case is
 * unchanged.
 */
export function drawableFromAccounts(accounts: ReadonlyArray<HealthThresholds>): HealthThresholds {
	const sole = (field: keyof HealthThresholds): number | null => {
		if (accounts.length === 0) return null;
		const first = accounts[0][field];
		if (first == null) return null;
		for (const a of accounts) {
			if (a[field] !== first) return null; // unset by someone, or a disagreement
		}
		return first;
	};
	return { comfort: sole('comfort'), floor: sole('floor') };
}

/**
 * Whether ANY in-scope account sets a threshold (#1646).
 *
 * The predicate behind the "we cannot band this" copy. Surfaces that never build
 * a `ForecastSeries` (the dashboard mini chart) ask this directly, so the
 * question is answered by one rule rather than by each surface's own guess.
 */
export function hasAnyThreshold(points: ReadonlyArray<BandablePoint>): boolean {
	const t = drawableThresholds(points);
	if (t.comfort != null || t.floor != null) return true;
	return (points[0]?.accounts ?? []).some((a) => a.comfort != null || a.floor != null);
}

/** Map a band to its resolved colour. */
export function healthBandColor(band: HealthBand, colors: HealthColors): string {
	if (band === 'comfort') return colors.forecast;
	if (band === 'caution') return colors.caution;
	return colors.danger;
}

interface BuildStopsOpts {
	/** Per-index alpha (e.g. horizon fade); applied via `applyAlpha`. */
	alphaForIndex?: (i: number) => number;
	/** Alpha applier (inject `colorWithAlpha`); required for `alphaForIndex` to take effect. */
	applyAlpha?: (color: string, alpha: number) => string;
}

/**
 * Ordered gradient stops for the health ramp — one stop per index at
 * `i / (n - 1)`, coloured by that index's band.
 *
 * `values` is still needed alongside `bands`: a null VALUE is missing data (fill
 * from a neighbour), while a null BAND is an unclassifiable week (draw it
 * neutral). Collapsing the two would repaint gaps as health.
 *
 *  - null values reuse the nearest known band colour (previous, else next).
 *  - all-null values ⇒ `[]`.
 *  - every band null ⇒ a single flat stop in the NEUTRAL tone (#1646).
 *  - `alphaForIndex` + `applyAlpha` fold a per-index alpha into each stop.
 */
export function buildHealthStops(
	values: ReadonlyArray<number | null>,
	bands: ReadonlyArray<HealthBand | null>,
	colors: HealthColors,
	opts: BuildStopsOpts = {}
): Array<{ offset: number; color: string }> {
	const n = values.length;
	if (n === 0) return [];
	const unbanded = colors.neutral ?? colors.forecast;
	if (bands.every((b) => b == null)) return [{ offset: 0, color: unbanded }];

	// Base band colour per index (null until resolved from a non-null neighbour).
	const base: (string | null)[] = values.map((v, i) => {
		if (v == null) return null;
		const band = bands[i];
		return band ? healthBandColor(band, colors) : unbanded;
	});
	// Fill nulls: carry previous forward, then back-fill any leading nulls.
	let last: string | null = null;
	for (let i = 0; i < n; i++) {
		if (base[i] != null) last = base[i];
		else if (last != null) base[i] = last;
	}
	let next: string | null = null;
	for (let i = n - 1; i >= 0; i--) {
		if (base[i] != null) next = base[i];
		else if (next != null) base[i] = next;
	}
	if (base.every((c) => c == null)) return [];

	const apply = opts.applyAlpha;
	const alphaFor = opts.alphaForIndex;
	return base.map((color, i) => {
		const c = color ?? unbanded;
		const finalColor = apply && alphaFor ? apply(c, alphaFor(i)) : c;
		return { offset: n === 1 ? 0 : i / (n - 1), color: finalColor };
	});
}

/**
 * Contiguous index spans that sit below comfort — the "tight week" ranges.
 * Inclusive endpoints; isolated single weeks yield `start === end`.
 *
 * Takes bands rather than a scalar comfort (#1646): with per-account thresholds
 * there is no single comfort number to compare against, and "below comfort" is
 * exactly what a `caution` or `danger` band already means. A `null` band breaks
 * a range — it is never "below". Callers mask the past by nulling those bands.
 */
export function detectTightRanges(
	bands: ReadonlyArray<HealthBand | null>
): Array<{ startIdx: number; endIdx: number }> {
	const ranges: Array<{ startIdx: number; endIdx: number }> = [];
	const n = bands.length;
	let start = -1;
	for (let i = 0; i < n; i++) {
		const b = bands[i];
		const below = b === 'caution' || b === 'danger';
		if (below && start === -1) start = i;
		if ((!below || i === n - 1) && start !== -1) {
			const end = below ? i : i - 1;
			ranges.push({ startIdx: start, endIdx: end });
			start = -1;
		}
	}
	return ranges;
}
